import React from 'react'
import { connect } from 'react-redux'
import styled from '@emotion/styled'
import { isNil } from 'lodash-es'
import {
  FatFingerProductSectorLimit,
  FatFingerDetail,
  CrudActions,
  CrudEventEnum,
  EntityType,
  ProductSector,
  CollateralBasketType
} from 'api'
import { ModalWindow, Groupbox, ModalFooter } from 'view/components/panels'
import { DialogRow, DialogField, NumericInputField } from 'view/components/inputs'
import { FatFingerSectorSelectboxField } from 'view/shared/entity'
import { TextButton, TextButtonType } from 'view/components/buttons'
import { NotificationType, ModalConsumerProps, withModal } from 'view/shared/modal'
import { notEmpty } from 'utils'
import { Translation, Locale } from 'locale'
import { createEntityGetterSelector } from 'selectors/entitySelectors'
import { createStructuredSelector } from 'reselect'

export interface FatFingerDialogProps extends ModalConsumerProps {
  fatFingerDetail: FatFingerDetail
  onClose?: () => unknown
  onUpsert: (fatFinger: FatFingerProductSectorLimit) => unknown
  onDelete: (fatFingerId: string) => unknown
  getProductSector: (productSectorId: string) => ProductSector | null
}

interface FatFingerDialogState {
  fatFingerDetail: FatFingerDetail
}

const mapStateToProps = createStructuredSelector({
  getProductSector: createEntityGetterSelector(EntityType.PRODUCT_SECTOR)
})

const mapDispatchToProps = {
  onUpsert: CrudActions.fatFingerUpsert,
  onDelete: CrudActions.fatFingerDelete
}

const Content = styled(Groupbox)`
  padding: 20px 20px 0 20px;

  & > *:last-child {
    border-bottom: none;
  }
`

export class FatFingerDialogComponent extends React.Component<FatFingerDialogProps> {
  state: FatFingerDialogState

  constructor(props: FatFingerDialogProps) {
    super(props)
    this.state = {
      fatFingerDetail: props.fatFingerDetail
    }
  }

  render() {
    const { fatFinger, availableEvents } = this.state.fatFingerDetail
    const isNewFatFinger = this.props.fatFingerDetail.fatFinger.productSectorId === ''
    const title = isNewFatFinger ? Locale.translate(Translation.TC_NEW_FAT_FINGER_LIMIT) : this.getTitle()
    const readOnly = !availableEvents.includes(CrudEventEnum.UPSERT)

    return (
      <ModalWindow title={title}>
        <Content>
          {isNewFatFinger && (
            <DialogRow>
              <DialogField>
                <FatFingerSectorSelectboxField
                  dataTestId={'FatFingerDialog-FarfingerSectorSelectboxField-Sector'}
                  label={Locale.translate(Translation.SC_SECTOR)}
                  onUpdate={this.updateHandler('productSectorId')}
                  readOnly={readOnly}
                  value={fatFinger.productSectorId}
                />
              </DialogField>
            </DialogRow>
          )}
          <DialogRow>
            <DialogField>
              <NumericInputField
                dataTestId={'FatFingerDialog-NumericInputField-FixedRatePrice'}
                label={Locale.translate(Translation.SC_FIXED_RATE_PRICE)}
                onUpdate={this.updateHandler('fixedRatePrice')}
                readOnly={readOnly}
                value={fatFinger.fixedRatePrice}
                formatOptions={{ minDp: 3, percent: true, decimalShift: 2 }}
              />
            </DialogField>
            <DialogField>
              <NumericInputField
                dataTestId={'FatFingerDialog-NumericInputField-FloatRateSpread'}
                label={Locale.translate(Translation.SC_FLOAT_RATE_SPREAD)}
                onUpdate={this.updateHandler('floatRateSpread')}
                readOnly={readOnly}
                value={fatFinger.floatRateSpread}
                formatOptions={{ decimalShift: 4, separator: false }}
              />
            </DialogField>
          </DialogRow>
          <DialogRow>
            <DialogField>
              <NumericInputField
                dataTestId={'FatFingerDialog-NumericInputField-FixedRateWarnLevel'}
                label={Locale.translate(Translation.SC_FIXED_RATE_WARN_LEVEL)}
                onUpdate={this.updateHandler('fixedRateWarnLevel')}
                readOnly={readOnly}
                value={fatFinger.fixedRateWarnLevel}
              />
            </DialogField>
            <DialogField>
              <NumericInputField
                dataTestId={'FatFingerDialog-NumericInputField-FloatRateWarnLevel'}
                label={Locale.translate(Translation.SC_FLOAT_RATE_WARN_LEVEL)}
                onUpdate={this.updateHandler('floatRateWarnLevel')}
                readOnly={readOnly}
                value={fatFinger.floatRateWarnLevel}
              />
            </DialogField>
          </DialogRow>
          <DialogRow>
            <DialogField>
              <NumericInputField
                dataTestId={'FatFingerDialog-NumericInputField-FixedRateRestriction'}
                label={Locale.translate(Translation.SC_FIXED_RATE_RESTRICTION)}
                onUpdate={this.updateHandler('fixedRateRestriction')}
                readOnly={readOnly}
                value={fatFinger.fixedRateRestriction}
              />
            </DialogField>
            <DialogField>
              <NumericInputField
                dataTestId={'FatFingerDialog-NumericInputField-FloatRateRestriction'}
                label={Locale.translate(Translation.SC_FLOAT_RATE_RESTRICTION)}
                onUpdate={this.updateHandler('floatRateRestriction')}
                readOnly={readOnly}
                value={fatFinger.floatRateRestriction}
              />
            </DialogField>
          </DialogRow>
          <DialogRow>
            <DialogField>
              <NumericInputField
                dataTestId={'FatFingerDialog-NumericInputField-NotionalAmountWarn'}
                label={Locale.translate(Translation.SC_NOTIONAL_AMOUNT_WARN)}
                onUpdate={this.updateHandler('notionalAmountWarn')}
                readOnly={readOnly}
                value={fatFinger.notionalAmountWarn}
                formatOptions={{ decimalShift: -6, separator: true }}
              />
            </DialogField>
            <DialogField>
              <NumericInputField
                dataTestId={'FatFingerDialog-NumericInputField-NotionalAmountRestriction'}
                label={Locale.translate(Translation.SC_NOTIONAL_AMOUNT_RESTRICTION)}
                onUpdate={this.updateHandler('notionalAmountRestriction')}
                readOnly={readOnly}
                value={fatFinger.notionalAmountRestriction}
                formatOptions={{ decimalShift: -6, separator: true }}
              />
            </DialogField>
          </DialogRow>
          <DialogRow>
            <DialogField>
              <NumericInputField
                dataTestId={'FatFingerDialog-NumericInputField-CashAmountWarn'}
                label={Locale.translate(Translation.SC_CASH_AMOUNT_WARN)}
                onUpdate={this.updateHandler('cashAmountWarn')}
                readOnly={readOnly}
                value={fatFinger.cashAmountWarn}
                formatOptions={{ decimalShift: -6, separator: true }}
              />
            </DialogField>
            <DialogField>
              <NumericInputField
                dataTestId={'FatFingerDialog-NumericInputField-CashAmountRestriction'}
                label={Locale.translate(Translation.SC_CASH_AMOUNT_RESTRICTION)}
                onUpdate={this.updateHandler('cashAmountRestriction')}
                readOnly={readOnly}
                value={fatFinger.cashAmountRestriction}
                formatOptions={{ decimalShift: -6, separator: true }}
              />
            </DialogField>
          </DialogRow>
        </Content>
        <ModalFooter>
          <DialogRow>
            <DialogField>
              <TextButton
                dataTestId={'FatFingerDialog-TextButton-ButtonCancel'}
                type={TextButtonType.Tertiary}
                onClick={this.props.onClose}>
                {Locale.translate(Translation.TC_BUTTON_CANCEL)}
              </TextButton>
            </DialogField>
            {!isNewFatFinger && (
              <DialogField flex={0}>
                <TextButton
                  dataTestId={'FatFingerDialog-TextButton-ButtonDelete'}
                  type={TextButtonType.Bad}
                  disabled={!this.canDelete()}
                  onClick={this.onDelete}>
                  {Locale.translate(Translation.TC_BUTTON_DELETE)}
                </TextButton>
              </DialogField>
            )}
            <DialogField flex={0}>
              <TextButton
                dataTestId={'FatFingerDialog-TextButton-ButtonSave'}
                type={TextButtonType.Primary}
                disabled={!this.canSave()}
                onClick={this.onSave}>
                {Locale.translate(Translation.TC_BUTTON_SAVE)}
              </TextButton>
            </DialogField>
          </DialogRow>
        </ModalFooter>
      </ModalWindow>
    )
  }

  private getTitle = () => {
    const { fatFinger, productSector, product } = this.state.fatFingerDetail
    return productSector && product ? `${product.name} - ${productSector.name}` : fatFinger.productSectorId
  }

  private updateHandler<K extends keyof FatFingerProductSectorLimit>(key: K) {
    return (value: FatFingerProductSectorLimit[K]) => {
      const update = {
        [key]: value
      }

      const fatFingerDetail = {
        ...this.state.fatFingerDetail,
        fatFinger: {
          ...this.state.fatFingerDetail.fatFinger,
          ...update
        }
      }

      this.setState({ fatFingerDetail })
    }
  }

  private canDelete = () => {
    return this.state.fatFingerDetail.availableEvents.includes(CrudEventEnum.DELETE)
  }

  private canSave = () => {
    const { fatFinger, availableEvents } = this.state.fatFingerDetail
    const isUpsertAvailable = availableEvents.includes(CrudEventEnum.UPSERT)
    const productSector = this.props.getProductSector(fatFinger.productSectorId)

    const isGCorEuroclear =
      !(productSector?.basketType === CollateralBasketType.GC_BESPOKE || productSector?.basketType === CollateralBasketType.EUROCLEAR) ||
      (!isNil(fatFinger.cashAmountWarn) && !isNil(fatFinger.cashAmountRestriction))

    return (
      isUpsertAvailable &&
      notEmpty(fatFinger.productSectorId) &&
      !isNil(fatFinger.fixedRatePrice) &&
      !isNil(fatFinger.fixedRateWarnLevel) &&
      !isNil(fatFinger.fixedRateRestriction) &&
      !isNil(fatFinger.notionalAmountWarn) &&
      !isNil(fatFinger.notionalAmountRestriction) &&
      isGCorEuroclear
    )
  }

  private onDelete = async () => {
    const { fatFinger, productSector } = this.state.fatFingerDetail

    const titleText = Locale.translate(Translation.TC_PROMPT_TITLE_DELETE_FAT_FINGER_LIMIT, { count: 1 })
    const messageText = Locale.translate(Translation.SC_PROMPT_MSG_DELETE_SINGLE_FAT_FINGER_LIMIT, {
      name: productSector ? productSector.name : fatFinger.productSectorId
    })

    const dialogArgs = {
      type: NotificationType.Confirm,
      titleText,
      messageText,
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      cancelButton: Translation.TC_BUTTON_NO,
      defaultButton: Translation.TC_BUTTON_YES
    }
    const dialogResult = await this.props.showDialog(dialogArgs)

    if (dialogResult === Translation.TC_BUTTON_YES) {
      this.props.onDelete(fatFinger.productSectorId)

      if (this.props.onClose) {
        this.props.onClose()
      }
    }
  }

  private onSave = () => {
    this.props.onUpsert(this.state.fatFingerDetail.fatFinger)

    if (this.props.onClose) {
      this.props.onClose()
    }
  }
}

export const FatFingerDialog = withModal(connect(mapStateToProps, mapDispatchToProps)(FatFingerDialogComponent))
